import React, { useEffect, useState } from 'react'
import { Box, Grid, styled, Theme, Typography } from '@mui/material'

import { Address } from '@ecm/data/Address'
import { State } from '@ecm/data/State'
import StateCard from '@ecm/ui/component/addresses/StateCard'
import Color from '@ecm/ui/element/Color'
import SearchInput from '@ecm/ui/element/input/Search'
import { useMapReferenceContext } from '@ecm/ui/provider/MapReferenceProvider'
import { useVisibleAddressesContext } from '@ecm/ui/provider/VisibleAddressesProvider'
import { updateMapBounds } from '@ecm/utils/map'

type StateListProps = {
  addresses: Address[]
  className?: string
  isMapVisible?: boolean
  states: State[]
}

type GroupedStates = {
  [group: string]: State[]
}

const StateList = styled(
  ({ states, addresses, isMapVisible = false, className }: StateListProps) => {
    const [visibleStates, setVisibleStates] = useState<State[]>(states)

    const {
      searchValue,
      setSearchValue,
      visibleAddresses,
      setIsFirstZoomAfterSearch,
      setVisibleAddresses,
    } = useVisibleAddressesContext()

    const mapReferenceContext = isMapVisible ? useMapReferenceContext() : undefined
    const mapReference = mapReferenceContext?.mapReference

    useEffect(() => {
      const visibleStates = visibleAddresses.map(address => address.state)
      const filteredStates = states.filter(state => visibleStates.includes(state.name))

      setVisibleStates(filteredStates)
    }, [visibleAddresses, states])

    useEffect(() => {
      if (isMapVisible) {
        setVisibleStates(visibleStates)
      } else {
        setVisibleStates(states)
        setVisibleAddresses(addresses)
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMapVisible])

    const onSearchInputChange = (value: string) => {
      if (value.length > 1) {
        const filteredAddresses = addresses.filter(address =>
          [address.city, address.state].some(name =>
            name.toLowerCase().startsWith(value.toLowerCase())
          )
        )

        setVisibleAddresses(filteredAddresses)
        setIsFirstZoomAfterSearch(true)
        setSearchValue(value)
      } else {
        setVisibleAddresses(addresses)
        setSearchValue('')
      }
    }

    const onSubmit = () => {
      if (!visibleAddresses.length || searchValue.length <= 1) {
        updateMapBounds(addresses, mapReference)

        return
      }

      updateMapBounds(visibleAddresses, mapReference)

      if (mapReference && visibleAddresses.length == 1) {
        mapReference.setZoom(5)
      }
    }

    const onSearchClear = () => {
      setVisibleStates(states)
      setVisibleAddresses(addresses)
      setIsFirstZoomAfterSearch(false)
      setSearchValue('')

      updateMapBounds(addresses, mapReference)
    }

    const groupedStates = visibleStates.reduce((result: GroupedStates, state: State) => {
      const group = state.code[0]

      return {
        ...result,
        [group]: [...(result[group] ?? []), state],
      }
    }, {})

    return (
      <Box margin="4em 0" className={className}>
        <Box className="searchBox">
          <SearchInput
            id="address-search-input"
            label="Search"
            onChange={onSearchInputChange}
            onClear={onSearchClear}
            onSubmit={onSubmit}
            placeholder="Search by city or state"
            value={searchValue}
          />
        </Box>
        {Object.entries(groupedStates).map(([group, states]) => (
          <React.Fragment key={group}>
            <Typography variant="h3" component="h2">
              {group}
            </Typography>
            <Grid key={group} container spacing={3} sx={{ py: '0.75em' }}>
              {states.map(state => {
                const stateMatchesSearchValue = state.name
                  .toLowerCase()
                  .startsWith(searchValue.toLowerCase())

                const filteredAreasBySearchValue = state.areas.filter(area =>
                  [area.cityDisplay].some(name =>
                    name.toLowerCase().startsWith(searchValue.toLowerCase())
                  )
                )
                const filteredAreasByVisibleAddresses = state.areas.filter(area =>
                  visibleAddresses.map(address => address.city).includes(area.cityDisplay)
                )

                const visibleAreas = searchValue
                  ? stateMatchesSearchValue
                    ? state.areas
                    : filteredAreasBySearchValue
                  : filteredAreasByVisibleAddresses

                return (
                  <Grid
                    key={state.code}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{ minHeight: { sm: '280px' } }}
                  >
                    <StateCard
                      key={state.name}
                      name={state.name}
                      areas={visibleAreas}
                      slug={state.slug}
                    />
                  </Grid>
                )
              })}
            </Grid>
          </React.Fragment>
        ))}
      </Box>
    )
  }
)(({ theme }: { theme: Theme }) => ({
  '.searchBox': {
    margin: '4em 0',
    zIndex: 1,
    [theme.breakpoints.down('md')]: {
      backgroundColor: Color.smoke.normal,
      margin: '0 -1.5em',
      padding: '1em 1.5em 1.5em',
      position: 'sticky',
      top: '4.5em',
    },
    [theme.breakpoints.down('sm')]: {
      margin: '0 -1em',
      padding: '1em 1em 1.5em',
    },
  },
}))

export default StateList
